/**
 * FEAT-040 / S-251 — Platform-operator tenant management console.
 *
 * Cross-tenant table: type + seat limits, editable inline per row. Seat
 * usage (editor_count, workspace_count) is informational only — not editable
 * (it derives from actual membership/workspace data). Client-side guard
 * mirrors the server 403 (usePlatformAdmin → redirect); GET/PATCH
 * /admin/tenants is the real enforcement.
 */

import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useApiMutation, useApiQuery } from "@/lib/api";
import { usePlatformAdmin } from "../components/AuthProvider";
import { tenantStatusLabel } from "@/lib/tenant-status";

type TenantType = "admin" | "internal" | "agency" | "marketing" | "solowriter" | "leader";

const TENANT_TYPES: TenantType[] = [
  "admin",
  "internal",
  "agency",
  "marketing",
  "solowriter",
  "leader",
];

interface Tenant {
  id: number;
  name: string;
  tenant_type: TenantType;
  max_admins: number | null;
  max_editors: number | null;
  max_workspaces: number | null;
  max_reviewers: number | null;
  created_at: string;
  // FEAT-106 / S-425 — suspended and reinstated with `npm run tenant-lifecycle`.
  status?: string;
  suspended_at?: string | null;
  purge_after?: string | null;
  editor_count: number;
  workspace_count: number;
}

interface TenantsResponse {
  data: Tenant[];
}

interface PatchBody {
  tenant_type?: TenantType;
  max_admins?: number | null;
  max_editors?: number | null;
  max_workspaces?: number | null;
  max_reviewers?: number | null;
}

/** null = unlimited; render as an empty input, parse "" back to null. */
function limitToInput(n: number | null): string {
  return n === null ? "" : String(n);
}
function inputToLimit(s: string): number | null {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
}

export function AdminTenantsView() {
  const isPlatformAdmin = usePlatformAdmin();
  const { data, isLoading, error, refetch } = useApiQuery<TenantsResponse>(
    ["admin-tenants"],
    "/admin/tenants",
    { enabled: isPlatformAdmin }
  );

  if (!isPlatformAdmin) return <Navigate to="/kanban" replace />;

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <header className="settings-header">
          <div>
            <h1 className="settings-title">Tenants</h1>
            <p className="settings-sub">
              Tenant type, seat limits, and usage across the platform.
            </p>
          </div>
        </header>

        {isLoading && <div className="panel-empty">Loading…</div>}
        {error && <div className="panel-empty">Could not load tenants.</div>}

        {data && !isLoading && (
          <table className="workspaces-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Status</th>
                <th>Type</th>
                <th>Admins limit</th>
                <th>Editors (usage / limit)</th>
                <th>Workspaces (usage / limit)</th>
                <th>Reviewers limit</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.data.map((t) => (
                <TenantRow key={t.id} tenant={t} onSaved={() => void refetch()} />
              ))}
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={9} className="panel-empty">
                    No tenants.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}

function TenantRow({ tenant, onSaved }: { tenant: Tenant; onSaved: () => void }) {
  const [type, setType] = useState<TenantType>(tenant.tenant_type);
  const [maxAdmins, setMaxAdmins] = useState(limitToInput(tenant.max_admins));
  const [maxEditors, setMaxEditors] = useState(limitToInput(tenant.max_editors));
  const [maxWorkspaces, setMaxWorkspaces] = useState(limitToInput(tenant.max_workspaces));
  const [maxReviewers, setMaxReviewers] = useState(limitToInput(tenant.max_reviewers));

  const mutation = useApiMutation<Tenant, PatchBody>(
    `/admin/tenants/${tenant.id}`,
    "PATCH",
    { onSuccess: onSaved }
  );

  const dirty = useMemo(() => {
    return (
      type !== tenant.tenant_type ||
      maxAdmins !== limitToInput(tenant.max_admins) ||
      maxEditors !== limitToInput(tenant.max_editors) ||
      maxWorkspaces !== limitToInput(tenant.max_workspaces) ||
      maxReviewers !== limitToInput(tenant.max_reviewers)
    );
  }, [type, maxAdmins, maxEditors, maxWorkspaces, maxReviewers, tenant]);

  function submit() {
    if (!dirty || mutation.isPending) return;
    const body: PatchBody = {};
    if (type !== tenant.tenant_type) body.tenant_type = type;
    if (maxAdmins !== limitToInput(tenant.max_admins)) body.max_admins = inputToLimit(maxAdmins);
    if (maxEditors !== limitToInput(tenant.max_editors)) body.max_editors = inputToLimit(maxEditors);
    if (maxWorkspaces !== limitToInput(tenant.max_workspaces)) {
      body.max_workspaces = inputToLimit(maxWorkspaces);
    }
    if (maxReviewers !== limitToInput(tenant.max_reviewers)) {
      body.max_reviewers = inputToLimit(maxReviewers);
    }
    mutation.mutate(body);
  }

  // useApiMutation's error type is always ApiError (never a plain Error) —
  // no instanceof narrowing needed. Prefer the server's `error` code (e.g.
  // "tenant_type_downgrade_not_allowed") over the generic message.
  let errorMessage: string | undefined;
  if (mutation.error) {
    let serverCode: string | undefined;
    try {
      serverCode = (JSON.parse(mutation.error.body || "{}") as { error?: string }).error;
    } catch {
      serverCode = undefined;
    }
    errorMessage = serverCode ?? mutation.error.message;
  }

  return (
    <tr>
      <td>{tenant.name}</td>
      <td>{tenantStatusLabel(tenant)}</td>
      <td>
        <select
          className="settings-select"
          value={type}
          onChange={(e) => setType(e.target.value as TenantType)}
        >
          {TENANT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input
          className="settings-input mono"
          style={{ width: 64 }}
          type="text"
          inputMode="numeric"
          placeholder="∞"
          value={maxAdmins}
          onChange={(e) => setMaxAdmins(e.target.value)}
        />
      </td>
      <td className="mono">
        {tenant.editor_count} /{" "}
        <input
          className="settings-input mono"
          style={{ width: 56, display: "inline-block" }}
          type="text"
          inputMode="numeric"
          placeholder="∞"
          value={maxEditors}
          onChange={(e) => setMaxEditors(e.target.value)}
        />
      </td>
      <td className="mono">
        {tenant.workspace_count} /{" "}
        <input
          className="settings-input mono"
          style={{ width: 56, display: "inline-block" }}
          type="text"
          inputMode="numeric"
          placeholder="∞"
          value={maxWorkspaces}
          onChange={(e) => setMaxWorkspaces(e.target.value)}
        />
      </td>
      <td>
        <input
          className="settings-input mono"
          style={{ width: 64 }}
          type="text"
          inputMode="numeric"
          placeholder="∞"
          value={maxReviewers}
          onChange={(e) => setMaxReviewers(e.target.value)}
        />
      </td>
      <td className="mono">{new Date(tenant.created_at).toLocaleDateString()}</td>
      <td>
        <button
          type="button"
          className="btn-ghost"
          disabled={!dirty || mutation.isPending}
          onClick={submit}
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </button>
        {mutation.isError && (
          <div className="settings-field-error" style={{ marginTop: 4 }}>
            {errorMessage}
          </div>
        )}
      </td>
    </tr>
  );
}
